
import React, { useState, useEffect } from 'react';
import ChannelSettings from './ChannelSettings';
import FrequencyManagement from './FrequencyManagement';
import RecorderDevices from './RecorderDevices';
import USBRecorders from './USBRecorders';
import { Radio, RadioTower, SmartphoneNfc, Usb } from 'lucide-react';
import { SettingsSubnav, SettingsSubnavTab } from './SettingsSubnav';
import {
  SettingsPageHero,
  SettingsSectionWidth,
  settingsMainCardClass,
} from './SettingsSectionLayout';

const ChannelsAndStationsSection = ({
  isDarkMode,
  edgeServerEndpoint,
  recordersEnabled,
  globalSettings,
}) => {
  const edgeRecordersEnabled = String(globalSettings?.enable_edge_devices || 'False').toLowerCase() === 'true';
  const usbRecordersEnabled = String(globalSettings?.enable_usb_audio_devices || 'False').toLowerCase() === 'true';

  const getInitialTab = () => {
    if (edgeRecordersEnabled) return 'recorders';
    if (usbRecordersEnabled) return 'usb-recorders';
    return 'channels';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());

  useEffect(() => {
    if (activeTab === 'recorders' && !edgeRecordersEnabled) {
      if (usbRecordersEnabled) {
        setActiveTab('usb-recorders');
      } else {
        setActiveTab('channels');
      }
    } else if (activeTab === 'usb-recorders' && !usbRecordersEnabled) {
      if (edgeRecordersEnabled) {
        setActiveTab('recorders');
      } else {
        setActiveTab('channels');
      }
    }
  }, [activeTab, edgeRecordersEnabled, usbRecordersEnabled]);

  const tabClassName = 'flex max-w-max shrink-0 items-center gap-1 whitespace-nowrap md:gap-2';
  const card = settingsMainCardClass(isDarkMode);

  return (
    <SettingsSectionWidth>
      <SettingsPageHero
        isDarkMode={isDarkMode}
        title="Recorders"
        description="Configure channels, frequencies, and connected recorders."
        icon={<span className="material-symbols-outlined text-2xl">mic_none</span>}
      />

      <div className={`rounded-xl border p-6 md:p-8 ${card}`}>
        <SettingsSubnav isDarkMode={isDarkMode} embedded aria-label="Recorder sections">
          {edgeRecordersEnabled && (
            <SettingsSubnavTab
              isDarkMode={isDarkMode}
              active={activeTab === 'recorders'}
              onClick={() => setActiveTab('recorders')}
              className={tabClassName}
            >
              <SmartphoneNfc className="h-4 w-4 shrink-0 md:h-5 md:w-5" />
              Edge Recorders
            </SettingsSubnavTab>
          )}
          {usbRecordersEnabled && (
            <SettingsSubnavTab
              isDarkMode={isDarkMode}
              active={activeTab === 'usb-recorders'}
              onClick={() => setActiveTab('usb-recorders')}
              className={tabClassName}
            >
              <Usb className="h-4 w-4 shrink-0 md:h-5 md:w-5" />
              USB Recorders
            </SettingsSubnavTab>
          )}
          <SettingsSubnavTab
            isDarkMode={isDarkMode}
            active={activeTab === 'channels'}
            onClick={() => setActiveTab('channels')}
            className={tabClassName}
          >
            <Radio className="h-4 w-4 shrink-0 md:h-5 md:w-5" />
            Channels
          </SettingsSubnavTab>
          <SettingsSubnavTab
            isDarkMode={isDarkMode}
            active={activeTab === 'stations'}
            onClick={() => setActiveTab('stations')}
            className={tabClassName}
          >
            <RadioTower className="h-4 w-4 shrink-0 md:h-5 md:w-5" />
            Stations
          </SettingsSubnavTab>
        </SettingsSubnav>

        {activeTab === 'recorders' && (
          <RecorderDevices
            edgeServerEndpoint={edgeServerEndpoint}
            isDarkMode={isDarkMode}
            enabled={recordersEnabled}
          />
        )}
        {activeTab === 'usb-recorders' && (
          <USBRecorders
            edgeServerEndpoint={edgeServerEndpoint}
            isDarkMode={isDarkMode}
            globalSettings={globalSettings}
          />
        )}
        {activeTab === 'channels' && (
          <ChannelSettings
            isDarkMode={isDarkMode}
            edgeServerEndpoint={edgeServerEndpoint}
          />
        )}
        {activeTab === 'stations' && (
          <FrequencyManagement
            edgeServerEndpoint={edgeServerEndpoint}
            isDarkMode={isDarkMode}
          />
        )}
      </div>
    </SettingsSectionWidth>
  );
};

export default ChannelsAndStationsSection;
